import { useState, useEffect } from "react";

const CONSENT_KEY = "qm_cookie_consent";

/**
 * LEGAL-2: Storage/cookie consent banner for the web SPA.
 *
 * Shows a bottom banner on first visit. User can accept (enables GA4 + Clarity)
 * or reject (analytics remain disabled). Preference stored in localStorage.
 *
 * Uses the same localStorage key as the landing page (`qm_cookie_consent`)
 * so consent carries across both apps on the same domain.
 */
export function StorageConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const pref = localStorage.getItem(CONSENT_KEY);
      if (!pref) setVisible(true);
      else if (pref === "accepted") loadAnalytics();
    } catch {
      setVisible(true);
    }
  }, []);

  function accept() {
    try { localStorage.setItem(CONSENT_KEY, "accepted"); } catch { /* ignore */ }
    setVisible(false);
    loadAnalytics();
  }

  function reject() {
    try { localStorage.setItem(CONSENT_KEY, "rejected"); } catch { /* ignore */ }
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 10000,
        background: "#0c1220",
        borderTop: "1px solid #1e2d45",
        padding: "16px 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        flexWrap: "wrap",
        animation: "fadeUp 0.3s ease",
      }}
    >
      <p style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.6, maxWidth: 600, margin: 0 }}>
        We use analytics (Google Analytics, Microsoft Clarity) to understand how citizens use this platform
        and improve the experience. No tracking without your consent.
      </p>
      <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
        <button
          onClick={accept}
          style={{
            padding: "8px 20px",
            background: "linear-gradient(135deg, #3b82f6, #2563eb)",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Accept
        </button>
        <button
          onClick={reject}
          style={{
            padding: "8px 20px",
            background: "transparent",
            color: "#64748b",
            border: "1px solid #1e2d45",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Decline
        </button>
      </div>
    </div>
  );
}

/**
 * Load GA4 + Clarity scripts dynamically (only after consent).
 * The env vars are baked in at build time by Vite.
 */
function loadAnalytics() {
  // GA4
  const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (gaId && !gaId.startsWith("%") && typeof window.gtag !== "function") {
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    gtag("js", new Date());
    gtag("config", gaId, { send_page_view: false, cookie_flags: "SameSite=None;Secure" });
    window.gtag = gtag;
  }

  // Microsoft Clarity
  const clarityId = import.meta.env.VITE_CLARITY_PROJECT_ID;
  if (clarityId && !clarityId.startsWith("%") && typeof window.clarity !== "function") {
    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", clarityId);
  }
}

/**
 * Check if analytics consent has been given.
 * Useful for conditional analytics calls.
 */
export function hasAnalyticsConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY) === "accepted";
  } catch {
    return false;
  }
}

/**
 * Reset consent (for "Manage Cookies" link in settings/footer).
 */
export function resetAnalyticsConsent() {
  try { localStorage.removeItem(CONSENT_KEY); } catch { /* ignore */ }
  window.location.reload();
}
